import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

// Inter-item gap in the visible row — must match the `gap` on the row in
// PrimaryHeaderLeftMenuItems.module.css (var(--cxy-spacing-2) = 8px).
const ITEM_GAP = 8;

interface UseMenuOverflowReturn<T> {
  containerRef: RefObject<HTMLDivElement | null>;
  dropdownItems: T[];
  mainItems: T[];
  measureRef: RefObject<HTMLUListElement | null>;
  showMoreDropdown: boolean;
}

/**
 * Splits `links` into the items that fit the available container width (`mainItems`) and the overflow
 * items (`dropdownItems`) that should render inside the MoreDropdown.
 *
 * Measures against a hidden, full-width copy of the list (`measureRef`) so widths are always readable
 * regardless of what the visible row currently renders — no width caching, no re-measure oscillation.
 * The last child of the measurement list must be the MoreDropdown trigger, so its width is reserved
 * only when overflow actually occurs.
 *
 * Client-only measurement: pre-JS the caller renders all items in a clipped row (no overlap), then this
 * hook collapses the overflow into MoreDropdown on hydrate. Mirrors the ResizeObserver pattern of
 * `useNavScrollState` without consuming it (that hook computes scroll-arrow state, not an item cutoff).
 */
export const useMenuOverflow = <T>(links: T[], itemGap: number = ITEM_GAP): UseMenuOverflowReturn<T> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);
  const [visibleCount, setVisibleCount] = useState(links.length);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const containerWidth = container.clientWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    if (children.length === 0) return;

    // Last measurement child is the MoreDropdown trigger; the rest are the nav items.
    const moreWidth = children[children.length - 1].offsetWidth;
    const itemWidths = children.slice(0, -1).map(child => child.offsetWidth);
    const itemCount = itemWidths.length;

    // Not laid out yet / hidden (0 width): render all, matching the SSR/no-JS baseline — the
    // ResizeObserver re-runs this once the container has a real width.
    if (containerWidth <= 0) {
      setVisibleCount(itemCount);
      return;
    }

    // Step 1 — everything fits (More not needed, so its width is not reserved).
    const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0) + itemGap * Math.max(0, itemCount - 1);
    if (totalWidth <= containerWidth) {
      setVisibleCount(itemCount);
      return;
    }

    // Step 2 — overflow: reserve the More trigger width, then fit as many items as possible.
    const available = containerWidth - moreWidth - itemGap;
    let used = 0;
    let count = 0;
    for (const width of itemWidths) {
      const next = used + (count > 0 ? itemGap : 0) + width;
      if (next > available) break;
      used = next;
      count += 1;
    }
    setVisibleCount(count);
  }, [itemGap]);

  useEffect(() => {
    recompute();

    const container = containerRef.current;
    if (!container) return;

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(recompute);
      resizeObserver.observe(container);
      if (measureRef.current) resizeObserver.observe(measureRef.current);
    } else {
      window.addEventListener('resize', recompute);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', recompute);
      }
    };
  }, [recompute, links]);

  return {
    containerRef,
    dropdownItems: links.slice(visibleCount),
    mainItems: links.slice(0, visibleCount),
    measureRef,
    showMoreDropdown: visibleCount < links.length,
  };
};
